import { useState, useEffect, useRef } from 'react';
import { Feature } from '@/types/feature.types';
import { FeatureCard } from './FeatureCard';
import { CAROUSEL_AUTO_ROTATE_INTERVAL } from '@/config/constants';
import styles from './FeatureCarousel.module.css';

interface FeatureCarouselProps {
  features: Feature[];
}

export function FeatureCarousel({ features }: FeatureCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const autoRotateInterval = useRef<NodeJS.Timeout | null>(null);

  // Auto-rotate functionality
  useEffect(() => {
    if (!isAutoRotating) return;

    autoRotateInterval.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % features.length);
    }, CAROUSEL_AUTO_ROTATE_INTERVAL);

    return () => {
      if (autoRotateInterval.current) {
        clearInterval(autoRotateInterval.current);
      }
    };
  }, [isAutoRotating, features.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    resetAutoRotate();
  };

  const resetAutoRotate = () => {
    setIsAutoRotating(false);
    setTimeout(() => setIsAutoRotating(true), CAROUSEL_AUTO_ROTATE_INTERVAL);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX;
    touchStartY.current = e.changedTouches[0].screenY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].screenX;
    const touchEndY = e.changedTouches[0].screenY;
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;

    // Only trigger swipe if horizontal movement is greater than vertical
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX < 0) {
        // Swipe left - next slide
        setCurrentSlide((prev) => (prev + 1) % features.length);
      } else {
        // Swipe right - previous slide
        setCurrentSlide((prev) => (prev - 1 + features.length) % features.length);
      }
      resetAutoRotate();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setCurrentSlide((prev) => (prev - 1 + features.length) % features.length);
        resetAutoRotate();
      } else if (e.key === 'ArrowRight') {
        setCurrentSlide((prev) => (prev + 1) % features.length);
        resetAutoRotate();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [features.length]);

  return (
    <div className={styles.container}>
      <div 
        className={styles.carousel}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={() => setIsAutoRotating(false)}
        onMouseLeave={() => setIsAutoRotating(true)}
        role="region"
        aria-label="Feature carousel"
        aria-roledescription="carousel"
      >
        <div 
          className={styles.track}
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          role="group"
          aria-label={`Slide ${currentSlide + 1} of ${features.length}`}
        >
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              feature={feature}
            />
          ))}
        </div>
      </div>
      
      <div className={styles.dots} role="group" aria-label="Carousel navigation">
        {features.map((_, index) => (
          <button
            key={index}
            className={`${styles.dot} ${index === currentSlide ? styles.active : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1} of ${features.length}`}
            aria-current={index === currentSlide ? 'true' : 'false'}
          />
        ))}
      </div>
    </div>
  );
}
